import React, { useMemo } from 'react';
import styles from './QuotationForm.module.scss';
import { MapPin, Locate, DollarSign, ClipboardCheck, CalendarDays, Clock } from 'lucide-react';

const formatDateRange = (start, end) => {
  if (!start && !end) return 'Sin fecha definida';
  const format = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formattedStart = format(start);
  const formattedEnd = format(end);
  if (formattedStart && formattedEnd) return `${formattedStart} - ${formattedEnd}`;
  return formattedStart || formattedEnd || 'Sin fecha definida';
};

const calculateRemainingDays = (end) => {
  if (!end) return null;
  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) return null;
  const today = new Date();
  const diff = endDate.getTime() - today.getTime();
  const days = Math.max(Math.ceil(diff / (1000 * 60 * 60 * 24)), 0);
  return days;
};

const QuotationForm = ({ quotation }) => {
  const quo = quotation?.quotation;

  const currencyLabel = useMemo(() => {
    if (!quo?.currency) return 'Sin especificar';
    if (quo.currency.toUpperCase() === 'ARS') return 'Pesos argentinos';
    if (quo.currency.toUpperCase() === 'USD') return 'Dólares estadounidenses';
    return quo.currency;
  }, [quo?.currency]);

  const periodLabel = useMemo(
    () => formatDateRange(quo?.startDate, quo?.endDate),
    [quo?.startDate, quo?.endDate]
  );

  const remainingDays = useMemo(() => calculateRemainingDays(quo?.endDate), [quo?.endDate]);

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <CalendarDays size={18} />
        <div className={styles.headerContent}>
          <span className={styles.headerEyebrow}>Información del pedido</span>
          <h2 className={styles.headerTitle}>Datos generales</h2>
          <p className={styles.headerDescription}>
            Revisa los detalles de la solicitud antes de cargar tu cotización.
          </p>
        </div>
      </header>

      <div className={styles.grid}>
        <div className={styles.gridItem}>
          <div className={styles.gridIcon}>
            <MapPin size={18} />
          </div>
          <div className={styles.gridContent}>
            <span className={styles.gridLabel}>Provincia</span>
            <span className={styles.gridValue}>{quo?.jurisdiccion?.name || 'Sin especificar'}</span>
          </div>
        </div>
        <div className={styles.gridItem}>
          <div className={styles.gridIcon}>
            <Locate size={18} />
          </div>
          <div className={styles.gridContent}>
            <span className={styles.gridLabel}>Ciudad</span>
            <span className={styles.gridValue}>{quo?.zone || 'Sin especificar'}</span>
          </div>
        </div>
        <div className={styles.gridItem}>
          <div className={styles.gridIcon}>
            <DollarSign size={18} />
          </div>
          <div className={styles.gridContent}>
            <span className={styles.gridLabel}>Moneda</span>
            <span className={styles.gridValue}>{currencyLabel}</span>
          </div>
        </div>
      </div>

      <div className={styles.timeline}>
        <div className={styles.timelineContent}>
          <span className={styles.timelineLabel}>Período de cotización</span>
          <span className={styles.timelineValue}>{periodLabel}</span>
        </div>
        <div className={styles.timelineTag}>
          <Clock size={16} />
          <span>
            {remainingDays === null ? 'Sin fecha límite' : `${remainingDays} ${remainingDays === 1 ? 'día' : 'días'} restantes`}
          </span>
        </div>
      </div>

      <div className={styles.description}>
        <div className={styles.descriptionHeader}>
          <ClipboardCheck size={16} />
          <span>Descripción del pedido</span>
        </div>
        <p className={styles.descriptionBody}>{quo?.description || 'Sin descripción cargada.'}</p>
      </div>
    </section>
  );
};

export default QuotationForm;
